import React, { useRef } from "react";
import { Ban, Type, Image, Video, FileText, MapPin, Upload, Loader2, CheckCircle2 } from "lucide-react";

import { Card, Label, Hint, TextInput, Counter } from "./ui";
import { countVariables } from "../templateUtils";

const FORMATS = [
  { value: "NONE", label: "None", icon: Ban },
  { value: "TEXT", label: "Text", icon: Type },
  { value: "IMAGE", label: "Image", icon: Image },
  { value: "VIDEO", label: "Video", icon: Video },
  { value: "DOCUMENT", label: "Document", icon: FileText },
  { value: "LOCATION", label: "Location", icon: MapPin },
];

const ACCEPT = {
  IMAGE: "image/jpeg,image/png",
  VIDEO: "video/mp4,video/3gpp",
  DOCUMENT: ".pdf",
};

const MediaUploader = ({ format, header, onChange, onUpload, uploading }) => {
  const inputRef = useRef(null);

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    onChange({ media_name: file.name, media_preview: URL.createObjectURL(file) });
    onUpload(file, (handle) => {
      onChange({ media_handle: handle || "" });
    });
  };

  return (
    <div>
      <Label required>Sample {format.toLowerCase()}</Label>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center transition hover:border-[#37a1d2] hover:bg-[#eaf4fa]/50"
      >
        {uploading ? (
          <Loader2 size={20} className="animate-spin text-[#147db5]" />
        ) : (
          <Upload size={20} className="text-slate-400" />
        )}
        <span className="text-[13px] font-medium text-slate-600">
          {uploading ? "Uploading…" : "Click to choose a file"}
        </span>
        <span className="text-[11px] text-slate-400">
          {format === "IMAGE" && "JPG or PNG"}
          {format === "VIDEO" && "MP4 (H.264, AAC)"}
          {format === "DOCUMENT" && "PDF"}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPT[format]}
        onChange={pick}
      />

      {header.media_name && (
        <div className="mt-2.5 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          {header.media_handle ? (
            <CheckCircle2 size={15} className="flex-shrink-0 text-emerald-500" />
          ) : (
            <Loader2 size={15} className="flex-shrink-0 animate-spin text-slate-400" />
          )}
          <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
            {header.media_name}
          </span>
          <button
            type="button"
            onClick={() =>
              onChange({ media_name: "", media_preview: "", media_handle: "" })
            }
            className="text-[11px] font-semibold text-red-500 hover:text-red-600"
          >
            Remove
          </button>
        </div>
      )}
      <Hint>Used only as an example for Meta’s review — not sent to customers.</Hint>
    </div>
  );
};

const HeaderCard = ({ header, onChange, onUpload, uploading }) => {
  const setFormat = (format) =>
    onChange({
      format,
      text: "",
      example: "",
      media_name: "",
      media_preview: "",
      media_handle: "",
    });

  const headerVars = countVariables(header.text);

  return (
    <Card className="space-y-4">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {FORMATS.map(({ value, label, icon: Icon }) => {
          const selected = header.format === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setFormat(value)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 transition ${
                selected
                  ? "border-[#147db5] bg-[#eaf4fa]/70 ring-1 ring-[#b9dff0]"
                  : "border-slate-200 hover:border-[#7cc4e8] hover:bg-slate-50"
              }`}
            >
              <Icon
                size={16}
                className={selected ? "text-[#147db5]" : "text-slate-500"}
              />
              <span className="text-[11px] font-medium text-slate-600">{label}</span>
            </button>
          );
        })}
      </div>

      {header.format === "TEXT" && (
        <div>
          <Label required>
            Header text
            <span className="ml-2 font-normal text-slate-400">
              <Counter value={header.text.length} max={60} />
            </span>
          </Label>
          <TextInput
            value={header.text}
            maxLength={60}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder="Your order is confirmed"
          />
          {headerVars > 0 && (
            <div className="mt-2.5">
              <Label required hint="sample value for {{1}}">
                Header example
              </Label>
              <TextInput
                value={header.example}
                onChange={(e) => onChange({ example: e.target.value })}
                placeholder="12345"
              />
            </div>
          )}
          <Hint>One variable ({"{{1}}"}) is allowed in a text header.</Hint>
        </div>
      )}

      {["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format) && (
        <MediaUploader
          format={header.format}
          header={header}
          onChange={onChange}
          onUpload={onUpload}
          uploading={uploading}
        />
      )}

      {header.format === "LOCATION" && (
        <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
          A map pin is shown as the header. The actual latitude, longitude and
          address are supplied when the message is sent.
        </p>
      )}

      {header.format === "NONE" && (
        <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
          This template has no header.
        </p>
      )}
    </Card>
  );
};

export default HeaderCard;
